/**
 * Students Redux Slice
 *
 * Manages student/user list state, filters, and selected student.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Login } from '../../apis/api-client';

interface StudentsState {
  students: Login[];
  selectedStudent: Login | null;
  loading: boolean;
  error: string | null;
  filters: {
    role: number | null;
    status: 'active' | 'inactive' | 'pending' | null;
    workspaceId: number | null;
    searchQuery: string;
  };
}

const initialState: StudentsState = {
  students: [],
  selectedStudent: null,
  loading: false,
  error: null,
  filters: {
    role: null,
    status: 'active',
    workspaceId: null,
    searchQuery: '',
  },
};

export const studentsSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    setStudents: (state, action: PayloadAction<Login[]>) => {
      state.students = action.payload;
      state.loading = false;
      state.error = null;
    },
    addStudent: (state, action: PayloadAction<Login>) => {
      state.students.push(action.payload);
    },
    updateStudent: (state, action: PayloadAction<Login>) => {
      const index = state.students.findIndex((s) => s.id === action.payload.id);
      if (index !== -1) {
        state.students[index] = action.payload;
      }
    },
    removeStudent: (state, action: PayloadAction<number>) => {
      state.students = state.students.filter((s) => s.id !== action.payload);
    },
    setSelectedStudent: (state, action: PayloadAction<Login | null>) => {
      state.selectedStudent = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setFilters: (state, action: PayloadAction<Partial<StudentsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
  },
});

export const { setStudents, addStudent, updateStudent, removeStudent, setSelectedStudent, setLoading, setError, setFilters, resetFilters } =
  studentsSlice.actions;

export default studentsSlice.reducer;
